import { useState } from 'react';
import {
  LABOR_SOURCE_LABEL,
  LABOR_WARNING_LABEL,
  type ResolvedLaborRate,
} from '@obraplan/core/domain/takeoff/labor-rate';
import { roundPrice } from '@obraplan/core/domain/takeoff/material-price';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { NumeroOpcionalInput } from './campos';
import {
  baseDaConta,
  deOndeVeio,
  seloDaFonte,
  TOM_DA_FONTE,
  type CustoDaMaoDeObra,
} from './custo-da-mao-de-obra';
import { precoUnitario } from './formato';

/**
 * O R$/h de um item de mão de obra — a célula que o mostra e o diálogo do provisório (Fase 29,
 * etapa 3 — RN-399, D11, D13).
 *
 * A célula serve à consolidação; o diálogo só abre para quem edita o quantitativo (D13). Quem
 * só vê lê o mesmo R$/h, a mesma fonte e o mesmo aviso — e não tem botão nenhum.
 */

/** A linha de mão de obra como o R$/h a enxerga: a chave da consolidação e o que a tela diz. */
export interface MaoDeObraDoCusto {
  /** `funcao:<id>` ou `descricao:<…>`. */
  readonly key: string;
  readonly description: string;
}

/**
 * O R$/h que vale, com a fonte e o aviso — ou "—", com o porquê.
 *
 * O selo diz de onde veio o número, e de quantas pessoas é a média; nunca só por cor (§4.4).
 * O aviso é o de `LABOR_WARNING_LABEL`, palavra por palavra.
 */
export function CustoDaHoraNaCelula({
  item,
  taxa,
  custo,
  podeEditar,
  definirProvisorio,
}: {
  item: MaoDeObraDoCusto;
  taxa: ResolvedLaborRate;
  custo: CustoDaMaoDeObra;
  podeEditar: boolean;
  definirProvisorio: (key: string, perHour: number | null) => Promise<boolean>;
}) {
  const [aberto, setAberto] = useState(false);
  const selo = seloDaFonte(taxa);
  const conteudo = (
    <span className="flex flex-col items-end gap-0.5">
      <span className="flex flex-wrap items-center justify-end gap-1.5">
        <span className={cn('tabular', taxa.perHour !== undefined && 'font-medium')}>
          {taxa.perHour === undefined ? '—' : precoUnitario(taxa.perHour)}
        </span>
        {taxa.source !== undefined && selo !== undefined && (
          <Badge
            tone={TOM_DA_FONTE[taxa.source]}
            title={LABOR_SOURCE_LABEL[taxa.source]}
            className="px-1.5 py-0 text-[11px]"
          >
            {selo}
          </Badge>
        )}
      </span>
      {taxa.warning !== undefined && (
        <span className="text-[11px] text-(--color-state-warn)">
          {LABOR_WARNING_LABEL[taxa.warning]}
        </span>
      )}
    </span>
  );

  if (!podeEditar) return conteudo;
  return (
    <>
      <button
        type="button"
        aria-label={`R$/h de ${item.description}`}
        className="-mx-1 rounded-md px-1 py-0.5 text-right hover:bg-(--color-surface-muted) focus-visible:outline-2 focus-visible:outline-(--color-brand)"
        onClick={() => setAberto(true)}
      >
        {conteudo}
      </button>
      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent>
          {/* Montado só aberto: reabrir começa do que está gravado, e não do que se digitou. */}
          {aberto && (
            <DecidirProvisorio
              item={item}
              taxa={taxa}
              custo={custo}
              definirProvisorio={definirProvisorio}
              onFechar={() => setAberto(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * O diálogo do R$/h: de onde ele veio, a base da conta e o provisório.
 *
 * ## O provisório só vale sem ninguém na folha (D11)
 *
 * Com gente da função na folha da obra — ou, sem ela, no sistema —, o R$/h é a média dela, e
 * o provisório fica guardado sem valer. O diálogo diz isso **antes** do campo: gravar um
 * número e ver a tabela não mudar seria o tipo de surpresa que faz alguém achar que o
 * sistema perdeu o que digitou.
 *
 * A gravação vai na hora para o servidor, e o diálogo fica aberto mostrando o R$/h relido:
 * quem acabou de pôr o provisório quer ver o número que ele deu.
 */
function DecidirProvisorio({
  item,
  taxa,
  custo,
  definirProvisorio,
  onFechar,
}: {
  item: MaoDeObraDoCusto;
  taxa: ResolvedLaborRate;
  custo: CustoDaMaoDeObra;
  definirProvisorio: (key: string, perHour: number | null) => Promise<boolean>;
  onFechar: () => void;
}) {
  const guardado = custo.taxas.provisionals.get(item.key);
  const [gravando, setGravando] = useState(false);
  const [provisorio, setProvisorio] = useState<number | undefined>(guardado);
  const semFuncao = item.key.startsWith('descricao:');
  const daFolha = taxa.source === 'OBRA' || taxa.source === 'SISTEMA';

  const gravar = async (perHour: number | null): Promise<boolean> => {
    setGravando(true);
    try {
      return await definirProvisorio(item.key, perHour);
    } finally {
      setGravando(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{`R$/h de ${item.description}`}</DialogTitle>
        <DialogDescription>{baseDaConta(custo)}</DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        {/* O que vale agora — o mesmo que a tabela mostra, com a fonte por extenso. */}
        <div className="flex flex-col gap-0.5 rounded-md border border-(--color-border-subtle) px-3 py-2">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span className="tabular font-semibold" data-custo-que-vale>
              {taxa.perHour === undefined ? 'Sem custo' : `${precoUnitario(taxa.perHour)}/h`}
            </span>
            {taxa.source !== undefined && (
              <Badge tone={TOM_DA_FONTE[taxa.source]}>{LABOR_SOURCE_LABEL[taxa.source]}</Badge>
            )}
          </p>
          <p className="text-xs text-(--color-content-muted)">{deOndeVeio(taxa, semFuncao)}</p>
          {taxa.warning !== undefined && taxa.warning !== 'NAO_E_HORA' && (
            <p className="text-xs text-(--color-state-warn)">{LABOR_WARNING_LABEL[taxa.warning]}</p>
          )}
        </div>

        {daFolha && (
          <p className="text-xs text-(--color-state-info)">
            {`O provisório só vale quando ninguém da função está na folha — hoje vale a ${
              taxa.source === 'OBRA' ? 'folha da obra' : 'média do sistema'
            }, e o provisório fica guardado sem entrar na conta.`}
          </p>
        )}

        <form
          aria-label="Provisório"
          className="flex flex-col gap-1"
          onSubmit={(event) => {
            event.preventDefault();
            if (provisorio === undefined) return;
            void gravar(roundPrice(provisorio));
          }}
        >
          <p className="text-xs text-(--color-content-muted)">
            {semFuncao
              ? 'O item não aponta uma função: o provisório é o R$/h dele até alguém escolher a função no serviço.'
              : 'O provisório é o R$/h da função na obra enquanto ninguém dela tiver salário cadastrado.'}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <NumeroOpcionalInput
              aria-label={`R$/h provisório de ${item.description}`}
              placeholder="R$/h"
              className="h-9 w-32 text-right"
              value={provisorio}
              onChange={setProvisorio}
            />
            <span className="text-xs text-(--color-content-muted)">R$/h</span>
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              disabled={
                gravando || provisorio === undefined || provisorio < 0 || provisorio === guardado
              }
            >
              Gravar provisório
            </Button>
          </div>
        </form>
      </div>

      <DialogFooter>
        {guardado !== undefined && (
          <Button
            variant="secondary"
            disabled={gravando}
            onClick={() => {
              setProvisorio(undefined);
              void gravar(null);
            }}
          >
            Tirar provisório
          </Button>
        )}
        <Button variant="ghost" onClick={onFechar}>
          Fechar
        </Button>
      </DialogFooter>
    </>
  );
}
